import { useMemo, useState } from 'react';
import { vuln } from '../services/api';

const TABS = [
  { id: 'vuln', label: 'Vulnerability Scan' },
  { id: 'nikto', label: 'Nikto Scanner' },
  { id: 'trace', label: 'Network Trace' },
];

const tones = {
  success: { background: 'rgba(34,197,94,0.12)', color: '#4ade80', border: '1px solid rgba(34,197,94,0.2)' },
  danger: { background: 'rgba(248,113,113,0.12)', color: '#f87171', border: '1px solid rgba(248,113,113,0.2)' },
  warning: { background: 'rgba(251,146,60,0.12)', color: '#fb923c', border: '1px solid rgba(251,146,60,0.2)' },
  info: { background: 'rgba(56,189,248,0.12)', color: '#38bdf8', border: '1px solid rgba(56,189,248,0.2)' },
  purple: { background: 'rgba(168,85,247,0.12)', color: '#c084fc', border: '1px solid rgba(168,85,247,0.2)' },
  neutral: { background: 'rgba(148,163,184,0.1)', color: 'var(--text-2)', border: '1px solid rgba(148,163,184,0.14)' },
};

function Badge({ label, tone = 'neutral' }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '6px 12px', borderRadius: 999, fontSize: '0.74rem', fontWeight: 800, whiteSpace: 'nowrap', ...tones[tone] }}>
      {label}
    </span>
  );
}

function severityTone(severity) {
  const value = String(severity || '').toLowerCase();
  if (value === 'critical') return 'purple';
  if (value === 'high') return 'danger';
  if (value === 'medium') return 'warning';
  if (value === 'low') return 'info';
  return 'neutral';
}

function normaliseTarget(value) {
  return String(value || '').trim().replace(/^https?:\/\//i, '').replace(/\/.*$/, '');
}

function errorMessage(error) {
  return error?.response?.data?.detail || error?.response?.data?.error || error?.response?.data?.message || error?.message || 'The request could not be completed.';
}

function StatCard({ label, value, accent = 'var(--text-1)' }) {
  return (
    <article style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 14, padding: 18 }}>
      <div style={{ color: 'var(--text-3)', fontSize: '0.69rem', fontWeight: 800, letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 8 }}>{label}</div>
      <div style={{ color: accent, fontSize: '1.45rem', fontWeight: 900, overflowWrap: 'anywhere' }}>{value}</div>
    </article>
  );
}

function Section({ title, subtitle, children, right }) {
  return (
    <section style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 18, padding: 26 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: subtitle ? 20 : 16 }}>
        <div>
          <h3 style={{ margin: 0, color: 'var(--text-1)', fontSize: '1.08rem', fontWeight: 800 }}>{title}</h3>
          {subtitle && <p style={{ margin: '6px 0 0', color: 'var(--text-3)', fontSize: '0.82rem', lineHeight: 1.55 }}>{subtitle}</p>}
        </div>
        {right}
      </div>
      {children}
    </section>
  );
}

function EmptyState({ children }) {
  return <div style={{ padding: 34, textAlign: 'center', color: 'var(--text-3)', border: '1px dashed var(--border)', borderRadius: 14 }}>{children}</div>;
}

function VulnerabilityResults({ result }) {
  const summary = result?.summary || {};
  const riskScore = Number(result?.risk_score || 0);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <Section title="Vulnerability Assessment" subtitle="Non-destructive HTTP, TLS, security-header and sensitive-path checks." right={<Badge label={`${result?.risk_level || 'Unknown'} — Score ${riskScore}`} tone={riskScore >= 75 ? 'danger' : riskScore >= 40 ? 'warning' : 'success'} />}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 }}>
          <StatCard label="Critical" value={summary.critical || 0} accent="#c084fc" />
          <StatCard label="High" value={summary.high || 0} accent="#f87171" />
          <StatCard label="Medium" value={summary.medium || 0} accent="#fb923c" />
          <StatCard label="Headers missing" value={summary.headers_missing || 0} accent="#38bdf8" />
          <StatCard label="Verified exposed paths" value={summary.paths_exposed || 0} accent="#fbbf24" />
        </div>
      </Section>

      <Section title="Security Headers">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {(result?.header_checks || []).map((check) => {
            const disclosureHeader = ['server', 'x-powered-by'].includes(String(check.key || '').toLowerCase());
            const healthy = disclosureHeader ? !check.present : check.present;
            return (
              <article key={check.key || check.header} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 18, background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 14, padding: 18 }}>
                <div>
                  <div style={{ color: 'var(--text-1)', fontSize: '0.9rem', fontWeight: 750 }}>{check.header}</div>
                  <div style={{ color: 'var(--text-3)', fontSize: '0.78rem', marginTop: 4, lineHeight: 1.45 }}>{check.description}</div>
                  {!healthy && check.fix && <div style={{ color: '#38bdf8', fontSize: '0.76rem', marginTop: 7 }}>Recommended fix: {check.fix}</div>}
                </div>
                <Badge label={disclosureHeader ? (check.present ? 'Disclosed' : 'Not disclosed') : (check.present ? 'Present' : 'Missing')} tone={healthy ? 'success' : severityTone(check.severity)} />
              </article>
            );
          })}
          {!result?.header_checks?.length && <EmptyState>No security-header results were returned.</EmptyState>}
        </div>
      </Section>

      {!!result?.ssl_findings?.length && (
        <Section title="SSL/TLS Analysis">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {result.ssl_findings.map((finding, index) => (
              <article key={`${finding.name}-${index}`} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 18, background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 14, padding: 18 }}>
                <div>
                  <div style={{ color: 'var(--text-1)', fontWeight: 750 }}>{finding.name}</div>
                  <div style={{ color: 'var(--text-3)', fontSize: '0.78rem', marginTop: 4 }}>{finding.detail}</div>
                </div>
                <Badge label={finding.severity} tone={severityTone(finding.severity)} />
              </article>
            ))}
          </div>
        </Section>
      )}

      {!!result?.path_findings?.length && (
        <Section title="Verified Exposed Paths" subtitle="A path should appear here only when the response contains matching product or content evidence.">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {result.path_findings.map((finding, index) => (
              <article key={`${finding.path}-${index}`} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 18, background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 14, padding: 18 }}>
                <div>
                  <div style={{ color: 'var(--text-1)', fontFamily: 'JetBrains Mono, monospace', fontWeight: 800 }}>{finding.path}</div>
                  <div style={{ color: 'var(--text-3)', fontSize: '0.78rem', marginTop: 4 }}>{finding.name}</div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <Badge label={String(finding.status_code)} tone="info" />
                  <Badge label={finding.severity} tone={severityTone(finding.severity)} />
                </div>
              </article>
            ))}
          </div>
        </Section>
      )}
    </div>
  );
}

function NiktoResults({ result }) {
  const summary = result?.summary || {};
  const riskScore = Number(result?.risk_score || 0);
  return (
    <Section title="Nikto-style Web Server Assessment" subtitle={`Completed in ${result?.scan_duration || 0}s — ${result?.total_findings || 0} finding(s).`} right={<Badge label={`${result?.risk_level || 'Unknown'} — Score ${riskScore}`} tone={riskScore >= 75 ? 'danger' : riskScore >= 40 ? 'warning' : 'success'} />}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, marginBottom: 20 }}>
        <StatCard label="Critical" value={summary.critical || 0} accent="#c084fc" />
        <StatCard label="High" value={summary.high || 0} accent="#f87171" />
        <StatCard label="Medium" value={summary.medium || 0} accent="#fb923c" />
        <StatCard label="Low" value={summary.low || 0} accent="#38bdf8" />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {(result?.findings || []).map((finding, index) => (
          <article key={`${finding.id}-${index}`} style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 14, padding: 18 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 18 }}>
              <div>
                <div style={{ color: 'var(--text-1)', fontWeight: 800, fontSize: '0.9rem' }}><span style={{ color: 'var(--text-3)', fontFamily: 'JetBrains Mono, monospace', marginRight: 10 }}>{finding.id}</span>{finding.title}</div>
                <div style={{ color: 'var(--text-2)', fontSize: '0.8rem', marginTop: 8, lineHeight: 1.5 }}>{finding.description}</div>
                {finding.reference && <div style={{ color: 'var(--text-3)', fontSize: '0.76rem', marginTop: 6 }}>{finding.reference}</div>}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <Badge label={finding.category} tone="info" />
                <Badge label={finding.severity} tone={severityTone(finding.severity)} />
              </div>
            </div>
          </article>
        ))}
        {!result?.findings?.length && <EmptyState>No web-server findings were returned.</EmptyState>}
      </div>
    </Section>
  );
}

function AddressList({ title, addresses }) {
  return (
    <article style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 14, padding: 18 }}>
      <div style={{ color: 'var(--text-3)', fontSize: '0.69rem', fontWeight: 800, letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 10 }}>{title}</div>
      {addresses?.length ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
          {addresses.map((address) => <code key={address} style={{ color: 'var(--text-1)', fontSize: '0.82rem', overflowWrap: 'anywhere' }}>{address}</code>)}
        </div>
      ) : <span style={{ color: 'var(--text-3)', fontSize: '0.8rem' }}>None returned</span>}
    </article>
  );
}

function TraceResults({ result }) {
  const trace = result?.transaction_trace || result?.packet_table || [];
  const dns = result?.dns || {};
  const tls = result?.tls || {};
  const certificate = tls?.certificate || {};
  const protocolTone = (protocol) => {
    const value = String(protocol || '').toUpperCase();
    if (value.includes('DNS')) return 'info';
    if (value.includes('TCP')) return 'success';
    if (value.includes('TLS')) return 'purple';
    if (value.includes('HTTPS') || value.includes('HTTP')) return 'warning';
    return 'neutral';
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <Section title="Network Transaction Trace" subtitle="Application-generated DNS, TCP, TLS and HTTPS connection evidence." right={<Badge label={`${trace.length} trace events`} tone="info" />}>
        <div style={{ padding: 16, borderRadius: 14, background: 'rgba(56,189,248,0.08)', border: '1px solid rgba(56,189,248,0.18)', color: 'var(--text-2)', fontSize: '0.8rem', lineHeight: 1.55, marginBottom: 18 }}>
          <strong style={{ color: '#38bdf8' }}>Evidence notice: </strong>{result?.disclaimer || 'This is an application-generated connection trace, not a raw PCAP capture.'}
        </div>
        <div style={{ overflowX: 'auto', border: '1px solid var(--border)', borderRadius: 14 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 920, fontSize: '0.8rem' }}>
            <thead><tr style={{ background: 'var(--bg-surface)' }}>{['No.', 'Time', 'Source', 'Destination', 'Protocol', 'Evidence', 'Details'].map((heading) => <th key={heading} style={{ textAlign: 'left', padding: '12px 14px', color: 'var(--text-3)', fontSize: '0.68rem', fontWeight: 800, letterSpacing: '0.05em', textTransform: 'uppercase' }}>{heading}</th>)}</tr></thead>
            <tbody>
              {trace.map((event, index) => (
                <tr key={`${event.no}-${event.time}-${event.info}-${index}`} style={{ borderTop: '1px solid var(--border)' }}>
                  <td style={{ padding: '12px 14px', color: 'var(--text-3)' }}>{event.no}</td>
                  <td style={{ padding: '12px 14px', color: 'var(--text-3)', fontFamily: 'JetBrains Mono, monospace' }}>{event.time}</td>
                  <td style={{ padding: '12px 14px', color: 'var(--text-2)' }}>{event.source}</td>
                  <td style={{ padding: '12px 14px', color: 'var(--text-2)' }}>{event.destination}</td>
                  <td style={{ padding: '12px 14px' }}><Badge label={event.protocol} tone={protocolTone(event.protocol)} /></td>
                  <td style={{ padding: '12px 14px' }}><Badge label={event.evidence_type || 'derived'} tone="neutral" /></td>
                  <td style={{ padding: '12px 14px', color: 'var(--text-1)', lineHeight: 1.45 }}>{event.info}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      <Section title="DNS Intelligence" subtitle={dns.resolver_note || 'Resolution performed through the system resolver.'} right={<Badge label={dns.status === 'resolved' ? 'Resolved' : 'Resolution failed'} tone={dns.status === 'resolved' ? 'success' : 'danger'} />}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 12, marginBottom: 14 }}>
          <StatCard label="Canonical name" value={dns.canonical_name || dns.query || 'N/A'} />
          <StatCard label="Selected endpoint" value={dns.selected_address || 'N/A'} accent="#38bdf8" />
          <StatCard label="Lookup time" value={`${dns.response_time_ms || 0} ms`} accent="#4ade80" />
          <StatCard label="Record count" value={dns.record_count || 0} accent="#c084fc" />
          <StatCard label="Reverse DNS" value={dns.reverse_dns || 'Unavailable'} />
          <StatCard label="DNSSEC" value={dns.dnssec_status || 'Not evaluated'} accent="#fbbf24" />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12 }}>
          <AddressList title="IPv4 addresses" addresses={dns.ipv4_addresses} />
          <AddressList title="IPv6 addresses" addresses={dns.ipv6_addresses} />
        </div>
      </Section>

      {tls.status === 'success' && (
        <Section title="TLS Session Intelligence" subtitle="Negotiated session metadata and a protocol-aware handshake model." right={<Badge label={tls.certificate_valid ? 'Certificate valid' : 'Review certificate'} tone={tls.certificate_valid ? 'success' : 'warning'} />}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 12, marginBottom: 18 }}>
            <StatCard label="TLS version" value={tls.tls_version || 'Unknown'} accent="#c084fc" />
            <StatCard label="Cipher suite" value={tls.cipher_suite || 'Unknown'} />
            <StatCard label="Cipher strength" value={`${tls.cipher_bits || 0}-bit`} accent="#38bdf8" />
            <StatCard label="Handshake time" value={`${tls.handshake_time_ms || 0} ms`} accent="#4ade80" />
            <StatCard label="Hostname verification" value={tls.hostname_valid ? 'Passed' : 'Failed'} accent={tls.hostname_valid ? '#4ade80' : '#f87171'} />
            <StatCard label="Forward secrecy" value={tls.forward_secrecy_likely ? 'Likely' : 'Not confirmed'} accent={tls.forward_secrecy_likely ? '#4ade80' : '#fbbf24'} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, marginBottom: 20 }}>
            <StatCard label="Certificate subject" value={certificate.subject_cn || 'N/A'} />
            <StatCard label="Certificate issuer" value={certificate.issuer_cn || certificate.issuer_org || 'N/A'} />
            <StatCard label="Valid from" value={certificate.not_before || 'N/A'} />
            <StatCard label="Valid until" value={certificate.not_after || 'N/A'} />
            <StatCard label="Days remaining" value={certificate.days_remaining ?? 'N/A'} accent={Number(certificate.days_remaining || 0) < 30 ? '#fb923c' : '#4ade80'} />
            <StatCard label="SAN entries" value={certificate.san_count || 0} accent="#38bdf8" />
          </div>
          <div style={{ color: 'var(--text-3)', fontSize: '0.7rem', fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 12 }}>Protocol-aware handshake stages</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {(tls.handshake_steps || []).map((step) => (
              <article key={`${step.step}-${step.message}`} style={{ display: 'grid', gridTemplateColumns: '38px minmax(150px, 220px) 1fr', gap: 14, alignItems: 'center', background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 14, padding: 14 }}>
                <span style={{ width: 30, height: 30, display: 'grid', placeItems: 'center', borderRadius: 999, background: 'rgba(168,85,247,0.14)', color: '#c084fc', fontWeight: 900, fontSize: '0.76rem' }}>{step.step}</span>
                <strong style={{ color: '#c084fc', fontSize: '0.84rem' }}>{step.message}</strong>
                <span style={{ color: 'var(--text-2)', fontSize: '0.8rem', lineHeight: 1.5 }}>{step.detail}</span>
              </article>
            ))}
          </div>
        </Section>
      )}

      {tls.status === 'failed' && <Section title="TLS Session Intelligence"><EmptyState>TLS inspection failed: {tls.error || 'Unknown error'}</EmptyState></Section>}
    </div>
  );
}

export default function PenTest() {
  const [target, setTarget] = useState('');
  const [tab, setTab] = useState('vuln');
  const [results, setResults] = useState({ vuln: null, nikto: null, trace: null });
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState('');
  const currentResult = results[tab];

  const actionLabel = useMemo(() => {
    if (scanning) return 'Running assessment...';
    if (tab === 'vuln') return 'Run Vulnerability Scan';
    if (tab === 'nikto') return 'Run Nikto-style Scan';
    return 'Generate Network Trace';
  }, [scanning, tab]);

  const runScan = async () => {
    if (scanning) return;
    const cleanTarget = normaliseTarget(target);
    if (!cleanTarget) {
      setError('Enter a domain you own or are authorised to assess.');
      return;
    }
    setScanning(true);
    setError('');
    try {
      let response;
      if (tab === 'vuln') response = await vuln.fullScan(cleanTarget);
      else if (tab === 'nikto') response = await vuln.niktoScan(cleanTarget);
      else response = await vuln.networkCapture(cleanTarget);
      const data = response.data;
      if (data?.error) throw new Error(data.error);
      setResults((previous) => ({ ...previous, [tab]: data }));
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      setScanning(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <header>
        <div style={{ color: '#38bdf8', fontSize: '0.72rem', fontWeight: 900, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 8 }}>Authorised security validation</div>
        <h2 style={{ margin: 0, color: 'var(--text-1)', fontSize: '1.75rem', fontWeight: 900 }}>Penetration Testing Suite</h2>
        <p style={{ margin: '8px 0 0', color: 'var(--text-3)', fontSize: '0.92rem', lineHeight: 1.55 }}>Perform non-destructive vulnerability checks, Nikto-style web-server analysis and application-generated network transaction tracing.</p>
      </header>

      <div style={{ padding: 16, borderRadius: 14, background: 'rgba(251,146,60,0.08)', border: '1px solid rgba(251,146,60,0.2)', color: 'var(--text-2)', fontSize: '0.82rem', lineHeight: 1.5 }}><strong style={{ color: '#fb923c' }}>Authorised testing only. </strong>Scan only systems you own or have explicit permission to assess.</div>

      <nav style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {TABS.map((item) => (
          <button key={item.id} type="button" onClick={() => { setTab(item.id); setError(''); }} style={{ padding: '11px 20px', borderRadius: 11, cursor: 'pointer', fontFamily: 'inherit', fontSize: '0.85rem', fontWeight: 750, color: tab === item.id ? '#c084fc' : 'var(--text-3)', background: tab === item.id ? 'rgba(168,85,247,0.13)' : 'transparent', border: tab === item.id ? '1px solid rgba(168,85,247,0.25)' : '1px solid transparent' }}>{item.label}</button>
        ))}
      </nav>

      <section style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 18, padding: 24 }}>
        <label htmlFor="pentest-target" style={{ display: 'block', color: 'var(--text-2)', fontSize: '0.78rem', fontWeight: 800, marginBottom: 9 }}>Domain or hostname</label>
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
          <input id="pentest-target" value={target} onChange={(event) => setTarget(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter') runScan(); }} placeholder="example.com" disabled={scanning} style={{ flex: '1 1 420px', minWidth: 0, padding: '15px 16px', borderRadius: 12, background: 'var(--bg-input)', border: '1px solid var(--border)', color: 'var(--text-1)', fontSize: '0.92rem', outline: 'none', fontFamily: 'inherit' }} />
          <button type="button" onClick={runScan} disabled={scanning} style={{ padding: '15px 24px', borderRadius: 12, border: 'none', background: 'linear-gradient(135deg, #a855f7, #6366f1)', color: 'white', fontFamily: 'inherit', fontSize: '0.86rem', fontWeight: 850, cursor: scanning ? 'not-allowed' : 'pointer', opacity: scanning ? 0.62 : 1, boxShadow: '0 6px 22px rgba(168,85,247,0.24)' }}>{actionLabel}</button>
        </div>
        <p style={{ margin: '10px 0 0', color: 'var(--text-3)', fontSize: '0.75rem' }}>Safe test target: example.com</p>
      </section>

      {error && <div role="alert" style={{ padding: 16, borderRadius: 14, background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.24)', color: '#f87171', fontWeight: 700 }}>{error}</div>}
      {!currentResult && !scanning && <EmptyState>Select a test, enter an authorised domain, and run the assessment.</EmptyState>}
      {tab === 'vuln' && currentResult && <VulnerabilityResults result={currentResult} />}
      {tab === 'nikto' && currentResult && <NiktoResults result={currentResult} />}
      {tab === 'trace' && currentResult && <TraceResults result={currentResult} />}
    </div>
  );
}